import {Component, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Workflow} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_CRUD_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ConditionGroup, ConditionVariable, compileCondition, conditionIssue, readCondition} from './visual/workflow-condition-model';
import {WorkflowConditionDialogComponent} from './visual/workflow-condition-dialog.component';

import {mappingNameIssue, VariableSelection, WorkflowMapping} from '../variables/workflow-variables';

export interface WorkflowStageConditionsComponent extends Translatable {}

/** Displays a step condition and edits a detached draft only through explicit confirmation. */
@Component({selector: 'app-workflow-stage-conditions', templateUrl: './workflow-stage-conditions.component.html',
  styleUrls: ['./workflow-stage-conditions.component.css']})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class WorkflowStageConditionsComponent implements OnChanges, OnDestroy {
  @Input() workflow: Workflow & {variables?: WorkflowMapping[]};
  @Input() stageIndex: number;
  @Input() stageKey: unknown;
  @Input() stageName = '';
  @Input() variables: ConditionVariable[] = [];
  @Input() createVariable: (pending: VariableSelection[]) => Promise<VariableSelection>;
  @Output() conditionChange = new EventEmitter<{expression: string; addedVariables: WorkflowMapping[]}>();
  public group: ConditionGroup;
  public warning = '';
  private modal: NgbModalRef;
  private loadedExpression: string;

  /** Injects modal management and the administration translations. */
  public constructor(private modals: NgbModal, @Optional() private translationService: TranslationService) {}

  /** Reads the form expression without mutating existing stored syntax. */
  public get expression(): string { return this.workflow?.stages?.[this.stageIndex]?.expression || ''; }

  /** Refreshes friendly labels and warnings when workflow mappings or step conditions change. */
  public ngOnChanges(): void {
    if (this.loadedExpression !== this.expression) {
      this.group = readCondition(this.expression);
      this.loadedExpression = this.expression;
    }
    this.warning = conditionIssue(this.group, this.variables);
    if (this.modal) {
      this.modal.componentInstance.variables = [...this.variables, ...this.modal.componentInstance.pendingVariables.map(item => item.descriptor)];
    }
  }

  /** Closes an orphaned draft when its step is removed or becomes the first step. */
  public ngOnDestroy(): void { this.modal?.dismiss(); }

  /** Opens an independent draft and protects against intervening changes to the stage or its expression. */
  public edit(): void {
    if (this.modal || this.stageIndex <= 0) { return; }
    const original = this.expression;
    const stageKey = this.stageKey;
    const group = readCondition(original);
    const initial = JSON.stringify(group);
    const modal = this.modals.open(WorkflowConditionDialogComponent, {
      centered: true, size: 'lg', windowClass: 'workflow-condition-modal', ariaLabelledBy: 'workflow-condition-title'
    });
    this.modal = modal;
    Object.assign(modal.componentInstance, {
      group, variables: [...this.variables], createVariable: this.createVariable, stageName: this.stageName, originalSource: original, existing: !!original
    });
    modal.result.then((draft: ConditionGroup) => {
      this.modal = null;
      const pending: VariableSelection[] = modal.componentInstance.pendingVariables || [];
      if (JSON.stringify(draft) === initial && !pending.length) { return; }
      if (this.stageIndex <= 0 || stageKey !== this.stageKey || original !== this.expression) {
        this.warning = 'Die Bedingungen wurden zwischenzeitlich geändert. Bitte öffnen Sie den Editor erneut.';
        return;
      }
      const mappings = [...(this.workflow.variables || [])];
      for (const item of pending) {
        if (mappingNameIssue(item.variable.name, mappings)) { return; }
        mappings.push(item.variable);
      }
      if (conditionIssue(draft, [...this.variables, ...pending.map(item => item.descriptor)])) { return; }
      this.conditionChange.emit({expression: compileCondition(draft), addedVariables: pending.map(item => item.variable)});
    }, () => { this.modal = null; });
  }
}
